import { Link } from "react-router-dom";
import { PAGE_HEROES } from "../content/page-copy";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  SectionHead,
  Card,
  buttonStyles,
  fadeUp,
  stagger,
  inView,
} from "@comai-portal/design-system";

/**
 * SecurityPage — the security posture, in the operator's language. (CO 069)
 * Threads into /trust (doubt → trust) and the legal pages. Real claims only,
 * sourced from the platform's actual security model.
 */
const HERO = PAGE_HEROES['security']!;

export function SecurityPage() {
  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      <Section>
        <Container width="prose" className="text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="ds-eyebrow mb-5">
              {HERO.eyebrow}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5vw,3.5rem)] leading-[1.08] text-balance"
            >
              {HERO.h1}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lead text-mute dark:text-mute-soft mt-6"
            >
              Agents act on your business, so the controls around them are the
              product, not an afterthought. Every action is scoped, logged, and
              reversible — and the standards an enterprise asks for are built
              in.
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      <Section tint="sunk">
        <Container>
          <SectionHead
            eyebrow="How it's secured"
            title="The controls operators actually ask about."
          />
          <motion.div
            {...inView}
            variants={stagger}
            className="mt-12 grid gap-5 md:grid-cols-2"
          >
            {[
              {
                k: "Scoped, auditable agent access",
                v: "Every agent acts under explicit scopes and autonomy limits you set. Confidence-scored decisions, a full audit trail, time-windowed rollback. No black box.",
              },
              {
                k: "OAuth 2.1 + PKCE, enterprise SSO",
                v: "The agent surface authenticates with OAuth 2.1 + PKCE per the MCP spec; enterprise SSO via OIDC (Okta, Azure AD, any provider). Per-tenant isolation throughout.",
              },
              {
                k: "Your data stays yours",
                v: "One tenant identity, one data boundary. Platforms compose inside your tenant — they don't fracture your customer and order data across vendors.",
              },
              {
                k: "Compliance posture",
                v: "SOC 2-ready audit trails, GDPR-aligned data handling, and a Data Processing Agreement available for review.",
              },
            ].map((it) => (
              <motion.div key={it.k} variants={fadeUp}>
                <Card hover className="h-full">
                  <h3 className="text-display-sm mb-2">{it.k}</h3>
                  <p className="text-mute dark:text-mute-soft text-sm">
                    {it.v}
                  </p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      <Section>
        <Container width="prose" className="text-center">
          <motion.div {...inView} variants={fadeUp}>
            <h2 className="text-display-md text-balance">
              Security is one half. Verifiability is the other.
            </h2>
            <p className="text-lead text-mute dark:text-mute-soft mt-5">
              Locking the system down protects you. Letting you check our claims
              earns your trust. Both, by design.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/trust"
                className={buttonStyles({ variant: "primary" })}
              >
                How we prove it →
              </Link>
              <Link to="/dpa" className={buttonStyles({ variant: "outline" })}>
                Read the DPA
              </Link>
            </div>
          </motion.div>
        </Container>
      </Section>
    </div>
  );
}
